import { redirect } from 'react-router';

import { getValidLanguage } from '@repo/utils/common';

import { appConfig, appEnv, supportedLocales, type AppLocale } from '@/shared/configs/app.config';

export const getLocaleFromParams = (params: { locale?: string }): AppLocale => {
  const localeParam = params.locale;
  if (localeParam && supportedLocales.includes(localeParam as AppLocale)) {
    return localeParam as AppLocale;
  }
  return appConfig.fallbackLanguage;
};

export const getLocaleFromPathname = (pathname: string): AppLocale => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0] ?? '';

  return getValidLanguage(firstSegment, supportedLocales, appConfig.fallbackLanguage);
};

export const getLocaleFromRequest = (request: Request): AppLocale => {
  const url = new URL(request.url);
  return getLocaleFromPathname(url.pathname);
};

export const fetchTranslations = async (lng: AppLocale) => {
  try {
    const res = await fetch(`${appEnv.VITE_API_URL}/config/${lng}/translations`);
    return res.json();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch translations:', error);
    return {};
  }
};

export const validateLocaleOrRedirect = (url: URL, paramsLocale?: string | null) => {
  if (paramsLocale && !supportedLocales.includes(paramsLocale as AppLocale)) {
    const parts = url.pathname.split('/').filter(Boolean);
    const strippedPath = parts[0] === paramsLocale ? `/${parts.slice(1).join('/')}` : url.pathname;
    throw redirect(strippedPath + url.search + url.hash);
  }
  return getLocaleFromPathname(url.pathname);
};

export const stripLocaleFromPathname = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);
  const firstSegment = segments[0] ?? '';

  if (supportedLocales.includes(firstSegment as AppLocale)) {
    const remainingPath = `/${segments.slice(1).join('/')}`;
    return remainingPath || '/';
  }

  return pathname;
};

export const getLocaleUrl = (locale: AppLocale, pathname: string): string => {
  const strippedPath = stripLocaleFromPathname(pathname);

  // Default locale doesn't need a prefix
  if (locale === appConfig.fallbackLanguage) {
    return strippedPath;
  }

  // Add locale prefix
  if (strippedPath === '/') {
    return `/${locale}`;
  }

  return `/${locale}${strippedPath}`;
};

export const getValidLocale = (locale?: string | null): AppLocale => {
  if (!locale) return appConfig.fallbackLanguage;
  return getValidLanguage(locale, supportedLocales, appConfig.fallbackLanguage);
};
